import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
} from '@tanstack/react-table';
import { 
  Eye, 
  Edit, 
  Power, 
  PowerOff, 
  Download,
  Search,
  X,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fastapiService } from '../../services/fastapiService';
import { format } from 'date-fns';

type DeleteProgressRow = {
  step: string;
  label: string;
  status: 'running' | 'ok' | 'error';
  error?: string;
};

interface User {
  id: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  is_active: boolean;
  email_verified?: boolean;
  created_at: string;
  workspace_count?: number;
}

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [deleteProgress, setDeleteProgress] = useState<null | {
    email: string;
    userId: string;
    rows: DeleteProgressRow[];
    phase: 'running' | 'done' | 'error';
    fatalMessage?: string;
    summaryNote?: string;
  }>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: any = { page, limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await fastapiService.getUsers(params);
      setUsers(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewDetails = useCallback((userId: string) => {
    navigate(`/admin/users/${userId}`);
  }, [navigate]);

  const handleToggleStatus = useCallback(async (userId: string, currentStatus: boolean) => {
    try {
      // TODO: Implement toggle status endpoint
      await fetchUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to update user status');
    }
  }, [fetchUsers]);

  const handleDeleteUser = useCallback(async (userId: string, userEmail: string) => {
    if (
      !window.confirm(
        `Permanently delete ${userEmail}? This cannot be undone.\n\n• Removes the user from Supabase Auth and user_profiles.\n• Deletes every workspace they own (all inbox data, contacts, integrations, and credentials in those workspaces).\n• Removes their memberships, connected email/WhatsApp/calendar integrations, API keys they created, sessions, and other account-linked rows.\n• In workspaces owned by others, their personal integrations and CRM rows tied to them are removed; the workspace itself is kept.`
      )
    ) {
      return;
    }

    setError('');
    setDeleteProgress({
      email: userEmail,
      userId,
      rows: [],
      phase: 'running',
    });

    try {
      await fastapiService.deleteUserWithProgress(userId, (ev: Record<string, unknown>) => {
        setDeleteProgress((prev) => {
          if (!prev) return prev;
          if (ev.type === 'step' && ev.phase === 'start') {
            return {
              ...prev,
              rows: [
                ...prev.rows,
                {
                  step: String(ev.step),
                  label: String(ev.label ?? ev.step),
                  status: 'running' as const,
                },
              ],
            };
          }
          if (ev.type === 'step' && ev.phase === 'done') {
            const rows = prev.rows.map((r) =>
              r.step === ev.step
                ? {
                    ...r,
                    status: (ev.ok ? 'ok' : 'error') as 'ok' | 'error',
                    error: ev.error != null ? String(ev.error) : undefined,
                  }
                : r
            );
            return { ...prev, rows };
          }
          if (ev.type === 'purge_summary' && ev.summary && typeof ev.summary === 'object') {
            const s = ev.summary as { owned_workspaces_deleted?: number };
            const n = s.owned_workspaces_deleted;
            return {
              ...prev,
              summaryNote:
                typeof n === 'number' ? `Removed ${n} owned workspace${n === 1 ? '' : 's'}.` : prev.summaryNote,
            };
          }
          if (ev.type === 'complete') {
            return { ...prev, phase: 'done' as const };
          }
          if (ev.type === 'fatal') {
            return {
              ...prev,
              phase: 'error' as const,
              fatalMessage: String(ev.error ?? 'Delete failed'),
            };
          }
          return prev;
        });
      });
      await fetchUsers();
    } catch (e: any) {
      const msg = e?.error || e?.message || 'Failed to delete user';
      setError(msg);
      setDeleteProgress((prev) =>
        prev
          ? { ...prev, phase: 'error', fatalMessage: msg }
          : { email: userEmail, userId, rows: [], phase: 'error', fatalMessage: msg }
      );
    }
  }, [fetchUsers]);

  const handleBulkToggleStatus = async (activate: boolean) => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;
    
    try {
      // TODO: Implement bulk toggle endpoint
      // For now, just refresh the data
      setRowSelection({});
      await fetchUsers();
    } catch (e: any) {
      setError(e?.message || 'Failed to update users');
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    const csv = [
      ['Email', 'Name', 'Role', 'Status', 'Created'],
      ...users.map(u => [
        u.email,
        u.full_name || '-',
        u.role || 'user',
        u.is_active ? 'Active' : 'Inactive',
        u.created_at ? format(new Date(u.created_at), 'yyyy-MM-dd') : '-'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            className="rounded border-admin-300 text-primary-600 focus:ring-primary-500"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="rounded border-admin-300 text-primary-600 focus:ring-primary-500"
          />
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: ({ column }) => (
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center space-x-1 hover:text-admin-900"
          >
            <span>Email</span>
            {column.getIsSorted() && (
              <span>{column.getIsSorted() === 'asc' ? '↑' : '↓'}</span>
            )}
          </button>
        ),
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-admin-900">{row.original.email}</div>
            {row.original.full_name && (
              <div className="text-xs text-admin-500 mt-0.5">{row.original.full_name}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: ({ column }) => (
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center space-x-1 hover:text-admin-900"
          >
            <span>Role</span>
            {column.getIsSorted() && (
              <span>{column.getIsSorted() === 'asc' ? '↑' : '↓'}</span>
            )}
          </button>
        ),
        cell: ({ row }) => {
          const role = row.original.role || 'user';
          return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
              role === 'admin' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {role}
            </span>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: ({ column }) => (
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center space-x-1 hover:text-admin-900"
          >
            <span>Status</span>
            {column.getIsSorted() && (
              <span>{column.getIsSorted() === 'asc' ? '↑' : '↓'}</span>
            )}
          </button>
        ),
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-xs ${
            row.original.is_active ? 'bg-green-100 text-green-700' : 'bg-admin-100 text-admin-700'
          }`}>
            {row.original.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        accessorKey: 'email_verified',
        header: 'Verified',
        cell: ({ row }) => (
          <div className="flex items-center space-x-1">
            {row.original.email_verified ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-700">Verified</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-xs text-red-700">Not Verified</span>
              </>
            )}
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => (
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="flex items-center space-x-1 hover:text-admin-900"
          >
            <span>Created</span>
            {column.getIsSorted() && (
              <span>{column.getIsSorted() === 'asc' ? '↑' : '↓'}</span>
            )}
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-admin-500">
            {row.original.created_at 
              ? format(new Date(row.original.created_at), 'MMM dd, yyyy')
              : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleViewDetails(row.original.id)}
              className="p-2 rounded hover:bg-admin-100 transition-colors text-admin-600 hover:text-admin-900"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleToggleStatus(row.original.id, row.original.is_active)}
              className={`p-2 rounded hover:bg-admin-100 transition-colors ${
                row.original.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'
              }`}
              title={row.original.is_active ? 'Deactivate' : 'Activate'}
            >
              {row.original.is_active ? (
                <PowerOff className="w-4 h-4" />
              ) : (
                <Power className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => handleDeleteUser(row.original.id, row.original.email)}
              className="p-2 rounded hover:bg-red-50 transition-colors text-red-600 hover:text-red-700"
              title="Delete User"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [handleViewDetails, handleToggleStatus, handleDeleteUser]
  );

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(total / limit),
  });

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-admin-900">Users</h1>
          <p className="mt-1 text-sm text-admin-600">
            Manage system users and their permissions
          </p>
        </div>
        <button
          onClick={handleExport}
          className="admin-button-secondary flex items-center space-x-2 px-4 py-2"
        >
          <Download className="w-4 h-4" />
          <span>Export</span>
        </button>
      </div>

      {/* Filters */}
      <div className="admin-card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-admin-400" />
              <input
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                placeholder="Search by email or name..."
                className="admin-input pl-10 w-full"
              />
            </div>
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
            className="admin-input w-48"
          >
            <option value="">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
            className="admin-input w-48"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(search || roleFilter || statusFilter) && (
            <button
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
                setPage(1);
              }}
              className="admin-button-secondary flex items-center space-x-2 px-3 py-2"
            >
              <X className="w-4 h-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedCount > 0 && (
        <div className="admin-card p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkToggleStatus(true)}
                className="admin-button-secondary flex items-center space-x-2 px-3 py-2 text-sm"
              >
                <Power className="w-4 h-4" />
                <span>Activate</span>
              </button>
              <button
                onClick={() => handleBulkToggleStatus(false)}
                className="admin-button-secondary flex items-center space-x-2 px-3 py-2 text-sm"
              >
                <PowerOff className="w-4 h-4" />
                <span>Deactivate</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* DataTable */}
      <div className="admin-card p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-admin-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-admin-500">
                    No users found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr 
                    key={row.id} 
                    className={`hover:bg-admin-50 ${row.getIsSelected() ? 'bg-blue-50' : ''}`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-admin-600">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} users
          </div>
          <div className="flex items-center gap-2">
            <button
              className="admin-button px-3 py-2"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-sm text-admin-600">
              Page {page} of {Math.ceil(total / limit)}
            </span>
            <button
              className="admin-button px-3 py-2"
              disabled={page >= Math.ceil(total / limit)}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {deleteProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            className="admin-card max-w-lg w-full max-h-[90vh] flex flex-col shadow-xl"
            role="dialog"
            aria-labelledby="delete-progress-title"
            aria-busy={deleteProgress.phase === 'running'}
          >
            <div className="p-4 border-b border-admin-200">
              <h2 id="delete-progress-title" className="text-lg font-semibold text-admin-900">
                Deleting user
              </h2>
              <p className="mt-1 text-sm text-admin-600 break-all">{deleteProgress.email}</p>
              {deleteProgress.summaryNote && (
                <p className="mt-2 text-sm font-medium text-admin-800">{deleteProgress.summaryNote}</p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[120px] max-h-[50vh]">
              {deleteProgress.rows.length === 0 && deleteProgress.phase === 'running' && (
                <div className="flex items-center gap-2 text-sm text-admin-600">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  <span>Starting…</span>
                </div>
              )}
              {deleteProgress.rows.map((row, idx) => (
                <div
                  key={`${row.step}-${idx}`}
                  className="flex gap-3 text-sm rounded-md border border-admin-100 bg-admin-50/80 px-3 py-2"
                >
                  <div className="shrink-0 pt-0.5">
                    {row.status === 'running' && (
                      <Loader2 className="w-4 h-4 animate-spin text-admin-500" />
                    )}
                    {row.status === 'ok' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {row.status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-admin-900 leading-snug">{row.label}</p>
                    {row.error && <p className="mt-1 text-xs text-red-600 break-words">{row.error}</p>}
                  </div>
                </div>
              ))}
              {deleteProgress.fatalMessage && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {deleteProgress.fatalMessage}
                </div>
              )}
              {deleteProgress.phase === 'done' && !deleteProgress.fatalMessage && (
                <div className="flex items-center gap-2 text-sm text-green-800 font-medium">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  <span>User deleted successfully.</span>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-admin-200 flex justify-end">
              <button
                type="button"
                className="admin-button-secondary px-4 py-2"
                disabled={deleteProgress.phase === 'running'}
                onClick={() => setDeleteProgress(null)}
              >
                {deleteProgress.phase === 'running' ? 'Working…' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
